import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '../ui/table';
import { Package, Plus, Pencil, Trash2, Send, Boxes, PackageCheck, PackageOpen } from 'lucide-react';
import { toast } from 'sonner';

const emptyItemForm = { name: '', description: '', unit: '', category: '', total_quantity: '', notes: '' };
const emptyAssignForm = { sales_person_name: '', quantity: '', assigned_date: new Date().toISOString().slice(0, 10), note: '' };

const SALES_PERSON_DATALIST_ID = 'mo-sales-person-suggestions';

export const MarketingInventoryTab = ({ api }) => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total_stock: 0, issued_stock: 0, remaining_stock: 0, item_count: 0 });
  const [salesPersonNames, setSalesPersonNames] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showItemDialog, setShowItemDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState(emptyItemForm);
  const [savingItem, setSavingItem] = useState(false);

  const [assigningItem, setAssigningItem] = useState(null);
  const [assignForm, setAssignForm] = useState(emptyAssignForm);
  const [savingAssign, setSavingAssign] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, summaryRes, namesRes] = await Promise.all([
        api.get('/marketing-inventory/items'),
        api.get('/marketing-inventory/summary').catch(() => ({ data: null })),
        api.get('/marketing-inventory/sales-person-names').catch(() => ({ data: { names: [] } })),
      ]);
      setItems(itemsRes.data.items || []);
      if (summaryRes.data) setSummary(summaryRes.data);
      setSalesPersonNames(namesRes.data.names || []);
    } catch (err) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreateItem = () => {
    setEditingItem(null);
    setItemForm(emptyItemForm);
    setShowItemDialog(true);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      name: item.name || '',
      description: item.description || '',
      unit: item.unit || '',
      category: item.category || '',
      total_quantity: String(item.total_quantity ?? ''),
      notes: item.notes || '',
    });
    setShowItemDialog(true);
  };

  const saveItem = async () => {
    if (!itemForm.name.trim() || itemForm.total_quantity === '') {
      toast.error('Name and total quantity are required');
      return;
    }
    setSavingItem(true);
    try {
      const payload = {
        name: itemForm.name.trim(),
        description: itemForm.description || undefined,
        unit: itemForm.unit || undefined,
        category: itemForm.category || undefined,
        total_quantity: parseInt(itemForm.total_quantity, 10) || 0,
        notes: itemForm.notes || undefined,
      };
      if (editingItem) {
        await api.patch(`/marketing-inventory/items/${editingItem.id}`, payload);
        toast.success('Item updated');
      } else {
        await api.post('/marketing-inventory/items', payload);
        toast.success('Item added');
      }
      setShowItemDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save item');
    } finally {
      setSavingItem(false);
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.name}" from inventory?`)) return;
    try {
      await api.delete(`/marketing-inventory/items/${item.id}`);
      toast.success('Item deleted');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete item');
    }
  };

  const openAssign = (item) => {
    setAssigningItem(item);
    setAssignForm(emptyAssignForm);
  };

  const saveAssign = async () => {
    if (!assignForm.sales_person_name.trim() || !assignForm.quantity) {
      toast.error('Enter the Sales Person\'s name and a quantity');
      return;
    }
    setSavingAssign(true);
    try {
      await api.post(`/marketing-inventory/items/${assigningItem.id}/assign`, {
        sales_person_name: assignForm.sales_person_name.trim(),
        quantity: parseInt(assignForm.quantity, 10) || 0,
        assigned_date: assignForm.assigned_date || undefined,
        note: assignForm.note || undefined,
      });
      toast.success('Stock assigned');
      setAssigningItem(null);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to assign stock');
    } finally {
      setSavingAssign(false);
    }
  };

  if (loading) {
    return <div className="py-12 text-center text-muted-foreground">Loading inventory…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <Card><CardContent className="p-4 flex items-center gap-3">
          <Boxes className="w-5 h-5 text-primary" />
          <div><div className="text-xs text-muted-foreground">Items</div><div className="text-xl font-bold">{summary.item_count}</div></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <Package className="w-5 h-5 text-primary" />
          <div><div className="text-xs text-muted-foreground">Total Stock</div><div className="text-xl font-bold">{summary.total_stock}</div></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <PackageCheck className="w-5 h-5 text-amber-500" />
          <div><div className="text-xs text-muted-foreground">Issued</div><div className="text-xl font-bold">{summary.issued_stock}</div></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <PackageOpen className="w-5 h-5 text-emerald-500" />
          <div><div className="text-xs text-muted-foreground">Remaining</div><div className="text-xl font-bold">{summary.remaining_stock}</div></div>
        </CardContent></Card>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-semibold">Inventory Items</h2>
        <Button onClick={openCreateItem} size="sm" data-testid="mo-add-item-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add Item
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Issued</TableHead>
                <TableHead className="text-right">Remaining</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No inventory items yet</TableCell></TableRow>
              ) : items.map((item) => (
                <TableRow key={item.id} data-testid={`mo-item-row-${item.id}`}>
                  <TableCell className="font-medium">
                    {item.name}
                    {item.description && <div className="text-xs text-muted-foreground">{item.description}</div>}
                  </TableCell>
                  <TableCell>{item.category ? <Badge variant="outline">{item.category}</Badge> : '—'}</TableCell>
                  <TableCell>{item.unit || '—'}</TableCell>
                  <TableCell className="text-right">{item.total_quantity}</TableCell>
                  <TableCell className="text-right">{item.issued_quantity}</TableCell>
                  <TableCell className="text-right font-medium">{item.remaining_quantity}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => openAssign(item)} disabled={item.remaining_quantity <= 0} data-testid={`mo-assign-btn-${item.id}`}>
                      <Send className="w-3.5 h-3.5 mr-1" /> Assign
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEditItem(item)} aria-label="Edit item">
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteItem(item)} aria-label="Delete item">
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add / Edit item */}
      <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Item' : 'Add Inventory Item'}</DialogTitle>
            <DialogDescription>Marketing Ops owns this inventory exclusively — Sales Persons cannot view or edit it.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} placeholder="e.g. Branded T-Shirts" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Input value={itemForm.category} onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })} placeholder="Merchandise" />
              </div>
              <div className="space-y-1.5">
                <Label>Unit</Label>
                <Input value={itemForm.unit} onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })} placeholder="pcs" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Total Quantity *</Label>
              <Input type="number" min="0" value={itemForm.total_quantity} onChange={(e) => setItemForm({ ...itemForm, total_quantity: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} rows={2} />
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={itemForm.notes} onChange={(e) => setItemForm({ ...itemForm, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemDialog(false)}>Cancel</Button>
            <Button onClick={saveItem} disabled={savingItem} data-testid="mo-save-item-btn">{savingItem ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign to Sales Person */}
      <Dialog open={!!assigningItem} onOpenChange={(open) => !open && setAssigningItem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Stock — {assigningItem?.name}</DialogTitle>
            <DialogDescription>
              {assigningItem?.remaining_quantity} remaining. This assignment is recorded for Marketing Ops tracking only and is never shown to the Sales Person.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Sales Person Name *</Label>
              <Input
                value={assignForm.sales_person_name}
                onChange={(e) => setAssignForm({ ...assignForm, sales_person_name: e.target.value })}
                placeholder="Type the Sales Person's name"
                list={SALES_PERSON_DATALIST_ID}
                data-testid="mo-assign-sales-person-input"
              />
              <datalist id={SALES_PERSON_DATALIST_ID}>
                {salesPersonNames.map((n) => <option key={n} value={n} />)}
              </datalist>
              <p className="text-xs text-muted-foreground">Most Sales Persons don't have a UBS account — just type their name.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Quantity *</Label>
                <Input type="number" min="1" max={assigningItem?.remaining_quantity} value={assignForm.quantity} onChange={(e) => setAssignForm({ ...assignForm, quantity: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input type="date" value={assignForm.assigned_date} onChange={(e) => setAssignForm({ ...assignForm, assigned_date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Note</Label>
              <Textarea value={assignForm.note} onChange={(e) => setAssignForm({ ...assignForm, note: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssigningItem(null)}>Cancel</Button>
            <Button onClick={saveAssign} disabled={savingAssign} data-testid="mo-confirm-assign-btn">{savingAssign ? 'Assigning…' : 'Assign'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
